import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Box } from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { useDebouncedValue } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { useIsMutating } from '@tanstack/react-query'

import { supportFormSchema } from '@/modules/support/components/SupportForm/SupportForm.schema'
import type { SupportFormSchema } from '@/modules/support/components/SupportForm/SupportForm.types'
import {
  SUPPORT_SUBJECT_OPTIONS,
  supportFormDefaultValues,
} from '@/modules/support/components/SupportForm/SupportForm.values'
import type { SupportLocationState } from '@/modules/support/routes/supportRouteConfig.types'

import { Button } from '@/components/Button/Button'
import { FlexInputWrapper } from '@/components/FlexInputWrapper/FlexInputWrapper'
import { SelectInput } from '@/components/SelectInput/SelectInput'
import { Text } from '@/components/Text/Text'
import { Textarea } from '@/components/Textarea/Textarea'

import {
  useSupportServiceSupportControllerCreateTicket,
  useSupportServiceSupportControllerSearch,
} from '@/__generated__/queries'

const SEARCH_MIN_LENGTH = 2
const SEARCH_DEBOUNCE_MS = 300

const SUBJECT_TO_REFERENCE_TYPE: Record<string, 'account' | 'transaction'> = {
  'Account issue': 'account',
  'Transaction issue': 'transaction',
}

export function SupportForm() {
  const isMutating = useIsMutating({
    exact: true,
  })

  const form = useForm<SupportFormSchema>({
    initialValues: supportFormDefaultValues,
    validate: zodResolver(supportFormSchema),
  })

  type ReferenceOption = {
    group: 'Accounts' | 'Transactions'
    label: string
    value: string
  }

  const [selectedOption, setSelectedOption] = useState<ReferenceOption | null>(
    null,
  )
  const [searchValue, setSearchValue] = useState('')
  const [debouncedQuery] = useDebouncedValue(searchValue, SEARCH_DEBOUNCE_MS)

  const location = useLocation()
  const navigate = useNavigate()
  const prefillReference = (location.state as SupportLocationState | null)
    ?.supportReference

  useEffect(() => {
    if (!prefillReference) return

    const { id, label, secondary, type } = prefillReference

    setSelectedOption({
      group: type === 'account' ? 'Accounts' : 'Transactions',
      label: secondary ? `${label} — ${secondary}` : label,
      value: `${type}:${id}`,
    })

    if (type === 'account') {
      form.setValues({
        accountId: id,
        subject: 'Account issue',
        transactionId: null,
      })
    } else {
      form.setValues({
        accountId: null,
        subject: 'Transaction issue',
        transactionId: id,
      })
    }
    // run once on mount when arriving with prefill state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const trimmedQuery = debouncedQuery.trim()

  const searchType = SUBJECT_TO_REFERENCE_TYPE[form.values.subject]
  const showReferenceField = !!searchType

  useEffect(() => {
    if (!selectedOption) return

    const currentType = selectedOption.value.split(':')[0]

    if (currentType !== searchType) {
      setSelectedOption(null)
      setSearchValue('')
      form.setValues({ accountId: null, transactionId: null })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchType])

  const { data: searchData } = useSupportServiceSupportControllerSearch(
    { query: trimmedQuery, type: searchType },
    undefined,
    {
      enabled: showReferenceField && trimmedQuery.length >= SEARCH_MIN_LENGTH,
    },
  )

  const referenceData = useMemo(() => {
    const results = searchData?.results ?? []

    const toOption = (item: (typeof results)[number]): ReferenceOption => ({
      group: item.type === 'account' ? 'Accounts' : 'Transactions',
      label: item.secondary ? `${item.label} — ${item.secondary}` : item.label,
      value: `${item.type}:${item.id}`,
    })

    const options = results.map(toOption)

    if (
      selectedOption &&
      !options.some(o => o.value === selectedOption.value)
    ) {
      options.unshift(selectedOption)
    }

    const accounts = options.filter(o => o.group === 'Accounts')
    const transactions = options.filter(o => o.group === 'Transactions')

    const groups: {
      group: string
      items: { label: string; value: string }[]
    }[] = []

    if (accounts.length)
      groups.push({
        group: 'Accounts',
        items: accounts.map(({ label, value }) => ({ label, value })),
      })

    if (transactions.length)
      groups.push({
        group: 'Transactions',
        items: transactions.map(({ label, value }) => ({ label, value })),
      })

    return groups
  }, [searchData, selectedOption])

  const handleReferenceChange = (value: string | null) => {
    if (!value) {
      setSelectedOption(null)
      form.setValues({ accountId: null, transactionId: null })

      return
    }

    const [type, idStr] = value.split(':')
    const id = Number(idStr)

    const fromResults = (searchData?.results ?? []).find(
      r => `${r.type}:${r.id}` === value,
    )

    if (fromResults) {
      setSelectedOption({
        group: fromResults.type === 'account' ? 'Accounts' : 'Transactions',
        label: fromResults.secondary
          ? `${fromResults.label} — ${fromResults.secondary}`
          : fromResults.label,
        value,
      })
    }

    if (type === 'account') {
      form.setValues({ accountId: id, transactionId: null })
    } else if (type === 'transaction') {
      form.setValues({ accountId: null, transactionId: id })
    }
  }

  const createTicketMutation = useSupportServiceSupportControllerCreateTicket({
    onSuccess: () => {
      notifications.show({
        color: 'green',
        message: 'We received your message and will get back to you soon.',
        title: 'Support request sent',
      })

      form.reset()
      setSelectedOption(null)
      setSearchValue('')

      if (location.state) {
        navigate(location.pathname, { replace: true, state: null })
      }
    },
  })

  const handleSubmit = form.onSubmit(values => {
    createTicketMutation.mutate({
      requestBody: {
        accountId: values.accountId ?? undefined,
        message: values.message,
        subject: values.subject,
        transactionId: values.transactionId ?? undefined,
      },
    })
  })

  return (
    <Box component="form" mt={8} noValidate onSubmit={handleSubmit}>
      <Text mb={24} variant="body_2">
        Have a question or need help? Send us a message and our team will get
        back to you.
      </Text>

      <FlexInputWrapper
        isLast
        leftInput={
          <SelectInput
            data={SUPPORT_SUBJECT_OPTIONS}
            label="Subject"
            placeholder="Select a subject"
            {...form.getInputProps('subject')}
            value={form.values.subject || null}
          />
        }
      />

      {showReferenceField && (
        <FlexInputWrapper
          isLast
          leftInput={
            <SelectInput
              clearable
              data={referenceData}
              filter={({ options }) => options}
              label={
                searchType === 'account'
                  ? 'Related account (optional)'
                  : 'Related transaction (optional)'
              }
              nothingFoundMessage={
                trimmedQuery.length >= SEARCH_MIN_LENGTH
                  ? 'No matches found'
                  : 'Type at least 2 characters to search'
              }
              onChange={handleReferenceChange}
              onSearchChange={setSearchValue}
              placeholder={
                searchType === 'account'
                  ? 'Search accounts'
                  : 'Search transactions'
              }
              searchValue={searchValue}
              searchable
              value={selectedOption?.value ?? null}
            />
          }
        />
      )}

      <FlexInputWrapper
        isLast
        leftInput={
          <Textarea
            autosize
            label="Message"
            minRows={5}
            placeholder="Describe your issue or question"
            {...form.getInputProps('message')}
          />
        }
      />

      <Button
        loading={!!isMutating}
        mt={{ base: 16, md: 100 }}
        type="submit"
        variant="primary"
      >
        Send
      </Button>
    </Box>
  )
}
